import React, { useRef, useEffect, useCallback } from 'react';
import TileRepresentation from './StepDesigns/TileRepresentation';
import VerticalTileRepresentation from './StepDesigns/VerticalTileRepresentation';
import ListRepresentation from './StepDesigns/ListRepresentation';
import ClassicFormRepresentation from './StepDesigns/ClassicFormRepresentation';
import SelectionCounter from './SelectionCounter';
import RadioButtonGroup from './RadioButtonGroup';

/**
 * Component for rendering items based on representation type
 */
const RenderItemsByType = ({ step, stepIndex, itemIndex, onSelectionChange, showValidationError, radioValidation }) => {
	const representationType = step.type || 3; // Default to classic form (3)
	
	// Create stable reference for onSelectionChange callback
	const stableOnSelectionChange = useRef(onSelectionChange);
	
	// Update reference when callback changes
	useEffect(() => {
		stableOnSelectionChange.current = onSelectionChange;
	}, [onSelectionChange]);
	
	// Wrapper for the callback using stable reference
	const onSelectionChangeHandler = useCallback((data) => {
		if (stableOnSelectionChange.current) {
			stableOnSelectionChange.current(data);
		}
	}, []);

	if (step.required  && step.min_count === 0) {
		step.min_count = 1;
	}
	
	// Check if count limits are set
	const hasCountLimits = (step.min_count > 0 || step.max_count < Infinity);
	
	// Check if we have checkboxes or radio buttons
	const hasCheckboxes = step.items && step.items.some(item => item.type === 9);
	const hasRadioButtons = step.items && step.items.some(item => item.type === 10);
	
	// Only use SelectionCounter for checkboxes with limits
	// For radio buttons, we don't apply max_count restriction
	const needsCounter = hasCountLimits && (hasCheckboxes || hasRadioButtons); ;
	
	// For Radio-Buttons
	const hasRequiredRadios = step.items && step.items.some(item => item.type === 10 && item.required);
	
	let content;
	
	// Prepare items for radio buttons before passing them
	const preparedItems = step.items ? step.items.map((item) => {
		// Ensure radio buttons are correctly configured
		if (item.type === 10) {
			return {
				...item,
				// Make sure the value prop is correctly set
				value: item.value !== undefined ? item.value : item.label
			};
		}
		return item;
	}) : [];
	
	switch (representationType) {
		case 1: // Horizontal tiles
			content = <TileRepresentation 
						items={preparedItems} 
						step={step} 
						stepIndex={stepIndex} 
						itemIndex={itemIndex} 
					  />;
			break;
		case 2: // Vertical tiles (new)
			content = <VerticalTileRepresentation 
						items={preparedItems} 
						step={step} 
						stepIndex={stepIndex} 
						itemIndex={itemIndex} 
					  />;
			break;
		case 4: // List (formerly type 2)
			content = <ListRepresentation 
						items={preparedItems} 
						step={step} 
						stepIndex={stepIndex} 
						itemIndex={itemIndex} 
					  />;
			break;
		case 3: // Classic Form
		default:
			content = <ClassicFormRepresentation 
						items={preparedItems} 
						step={step} 
						stepIndex={stepIndex} 
						itemIndex={itemIndex} 
					  />;
	}
	
	// If we need validation logic, wrap the content accordingly
	if (needsCounter) {
		// Update: Pass maxReached information to parent component
		
		// For checkbox groups with min_count/max_count
		content = (
			<SelectionCounter 
				step={{...step, stepNumber: stepIndex + 1}}
				onSelectionChange={onSelectionChangeHandler}
				showValidationError={showValidationError}
			>
				{content}
			</SelectionCounter>
		);
	}
	
	// For required radio groups
	if (hasRequiredRadios) {
		const radioGroupName = `multi-${stepIndex}-${itemIndex}`;
		const isGroupValid = radioValidation?.[radioGroupName]?.isValid !== false; // If undefined or true, then valid
		
		content = (
			<RadioButtonGroup
				isValid={isGroupValid}
				showValidationError={showValidationError}
				name={radioGroupName}
			>
				{content}
			</RadioButtonGroup>
		);
	}
	
	return content;
};

export default React.memo(RenderItemsByType); // Add memo to prevent unnecessary re-renders
